import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import useRoutes from '../../../hooks/useRoutes';
import { GlobalLocationStateType, OpenMenu } from '../../../types/types';
import { ActiveMenu, computeActiveMenu } from '../utils/computeActiveMenu';
import { resolveStableActiveMenu } from '../utils/resolveStableActiveMenu';
import { useStandaloneFormPath } from './useStandaloneFormPath';

type UseActiveMenuProps = {
  menuItems: any[];
  peersets?: any[];
};

// Keeps the side menu in sync with the current route: highlights the active item, opens the
// block/submenu that contains it, and exposes toggle handlers for manual expand/collapse.
//
// The route-active block is derived SYNCHRONOUSLY (useMemo), so openMenu is correct on the same render
// the route changes — no lag where the active block collapses (which used to desync Ionic's accordion).
// A manual expand/collapse is a separate override that resets on navigation.
export const useActiveMenu = ({ menuItems, peersets }: UseActiveMenuProps) => {
  const routes = useRoutes();
  const { pathname } = useLocation<GlobalLocationStateType>();
  const standaloneFormPath = useStandaloneFormPath();

  const isDashboards = pathname === routes.dashboards();
  const matchSource = standaloneFormPath ?? pathname;

  // Last resolved active menu, kept across a peerset switch's transient `peersets === undefined` gap
  // so the highlight/open block doesn't clear for a frame (the flicker).
  const prevActiveRef = useRef<ActiveMenu | null>(null);

  const routeActive = useMemo(() => {
    if (isDashboards) {
      const dashboardsActive: ActiveMenu = {
        openMenu: [undefined, undefined],
        selectedItem: routes.dashboards(),
      };
      prevActiveRef.current = dashboardsActive;
      return dashboardsActive;
    }
    const computed = computeActiveMenu(
      menuItems,
      matchSource,
      routes,
      peersets
    );
    const stable = resolveStableActiveMenu(
      computed,
      prevActiveRef.current,
      !!peersets && peersets.length > 0
    );
    prevActiveRef.current = stable;
    return stable;
  }, [isDashboards, menuItems, matchSource, peersets, routes]);

  // Manual expand/collapse overrides the route-active open state until the next navigation.
  const [manualOpen, setManualOpen] = useState<OpenMenu | null>(null);
  // Pending "scroll the expanded block into view" timer (see toggleOpenMenu). Kept in a ref so we can
  // cancel it — the Sidebar is persistent, so a stale timer would otherwise scroll the menu on a later route.
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const prevPathnameRef = useRef(pathname);
  if (prevPathnameRef.current !== pathname) {
    prevPathnameRef.current = pathname;
    setManualOpen(null);
    // Navigation cancels any pending expand-scroll so it can't yank the new route's menu.
    clearTimeout(scrollTimeoutRef.current);
  }

  const openMenu = manualOpen ?? routeActive.openMenu;
  const selectedItem = routeActive.selectedItem;
  // menuItems are present whenever the Sidebar is rendered, so the menu is ready immediately.
  const sidebarLoaded = true;

  // Re-open the active item's block/submenu (used after clearing the search) by dropping any manual override.
  const openActiveMenu = useCallback(() => setManualOpen(null), []);

  const toggleOpenMenu = (menuKey) => {
    const oldOpenMenu = manualOpen ?? routeActive.openMenu;
    setManualOpen((prev) => {
      const base = prev ?? routeActive.openMenu;
      const next = [...base];
      next[0] = next[0] === menuKey ? undefined : menuKey;
      return next;
    });
    if (
      oldOpenMenu[0] !== undefined &&
      parseInt(oldOpenMenu[0]) < parseInt(menuKey)
    ) {
      // Wait for the accordion to expand, then bring it into view. Cancel any prior pending
      // scroll so rapid toggles don't stack and a navigation can abort it (see pathname guard).
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        document?.getElementById(`menu_block_${menuKey}`)?.scrollIntoView();
      }, 320);
    }
  };

  const toggleOpenSubmenu = (subMenuKey) => {
    setManualOpen((prev) => {
      const base = prev ?? routeActive.openMenu;
      const next = [...base];
      next[1] = next[1] === subMenuKey ? undefined : subMenuKey;
      return next;
    });
  };

  return {
    openMenu,
    sidebarLoaded,
    selectedItem,
    toggleOpenMenu,
    toggleOpenSubmenu,
    openActiveMenu,
  };
};
